import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import customDocumentService from '../../services/api/customDocumentService';
import type {
  CustomPlaceholder,
  CustomPlaceholderType,
  CustomDocumentConfig,
} from '../../types/customDocuments';

function deriveId(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/^[^a-z]+/, '');
}

interface CustomPlaceholderFormProps {
  clientId: string;
  placeholder?: CustomPlaceholder;  // undefined = create mode
  onSave: (config: CustomDocumentConfig) => void;
  onClose: () => void;
}

const CustomPlaceholderForm: React.FC<CustomPlaceholderFormProps> = ({
  clientId,
  placeholder,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const isEditMode = !!placeholder;

  const [label, setLabel] = useState(placeholder?.label || '');
  const [id, setId] = useState(placeholder?.id || '');
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  const [type, setType] = useState<CustomPlaceholderType>(placeholder?.type || 'TEXT');
  const [value, setValue] = useState(placeholder?.value || '');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-derive ID from label in create mode
  useEffect(() => {
    if (!isEditMode && !idManuallyEdited) {
      setId(deriveId(label));
    }
  }, [label, isEditMode, idManuallyEdited]);

  // Load existing image in edit mode
  useEffect(() => {
    if (isEditMode && placeholder?.type === 'IMAGE' && placeholder?.storagePath) {
      customDocumentService.getPlaceholderImageUrl(clientId, placeholder.id)
        .then(({ signedUrl }) => setImagePreview(signedUrl))
        .catch(() => {}); // Silently fail — image just won't show
    }
  }, [isEditMode, clientId, placeholder]);

  // Clean up object URL on unmount (only for locally-created blob URLs)
  const [localBlobUrl, setLocalBlobUrl] = useState<string | null>(null);
  useEffect(() => {
    return () => {
      if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
    };
  }, [localBlobUrl]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (2 MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(t('admin.customDocuments.placeholders.form.errors.imageTooLarge', 'Image file must be 2 MB or less'));
      return;
    }

    // Validate type
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError(t('admin.customDocuments.placeholders.form.errors.imageTypeInvalid', 'Only PNG and JPEG images are allowed'));
      return;
    }

    setImageFile(file);
    const blobUrl = URL.createObjectURL(file);
    setImagePreview(blobUrl);
    setLocalBlobUrl(blobUrl);
    setError(null);
  };

  const handleSave = async () => {
    if (!label.trim()) {
      setError(t('admin.customDocuments.placeholders.form.errors.labelRequired', 'Label is required'));
      return;
    }
    if (label.length > 100) {
      setError(t('admin.customDocuments.placeholders.form.errors.labelTooLong', 'Label must be 100 characters or less'));
      return;
    }
    if (!isEditMode && !id.trim()) {
      setError(t('admin.customDocuments.placeholders.form.errors.idRequired', 'Token ID is required'));
      return;
    }
    if (type !== 'IMAGE' && !value.trim()) {
      setError(t('admin.customDocuments.placeholders.form.errors.valueRequired', 'Value is required'));
      return;
    }
    if (type === 'IMAGE' && !isEditMode && !imageFile) {
      setError(t('admin.customDocuments.placeholders.form.errors.imageRequired', 'Image file is required'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (isEditMode && placeholder) {
        await customDocumentService.updatePlaceholder(clientId, placeholder.id, {
          label: label.trim(),
          value: type !== 'IMAGE' ? value.trim() : undefined,
        });
      } else {
        await customDocumentService.createPlaceholder(clientId, {
          id: id.trim(),
          label: label.trim(),
          type,
          value: type !== 'IMAGE' ? value.trim() : undefined,
        });
      }

      // Upload image if provided
      if (imageFile && (isEditMode ? placeholder?.id : id)) {
        const placeholderId = isEditMode ? placeholder!.id : id.trim();
        await customDocumentService.uploadPlaceholderImage(clientId, placeholderId, imageFile);
      }

      // Always re-fetch the full config (create/update return a single placeholder, not the full config)
      const config = await customDocumentService.getConfig(clientId);
      onSave(config);
    } catch (err: any) {
      setError(err.message || t('admin.customDocuments.placeholders.form.errors.saveFailed', 'Failed to save placeholder'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="placeholder-form-panel">
      <div className="form-panel-header">
        <h4>{isEditMode
          ? t('admin.customDocuments.placeholders.form.titleEdit', 'Edit Placeholder')
          : t('admin.customDocuments.placeholders.form.titleCreate', 'Add Placeholder')}</h4>
        <button type="button" className="btn-close" onClick={onClose}>&times;</button>
      </div>

      <div className="form-panel-body">
        <div className="form-group">
          <label>{t('admin.customDocuments.placeholders.form.label', 'Label')}</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={100}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label>{t('admin.customDocuments.placeholders.form.id', 'Token ID')}</label>
          {isEditMode ? (
            <input type="text" value={id} readOnly className="form-input readonly" />
          ) : (
            <input
              type="text"
              value={id}
              onChange={(e) => { setId(e.target.value); setIdManuallyEdited(true); }}
              maxLength={100}
              className="form-input"
            />
          )}
          <span className="form-hint">
            {isEditMode
              ? t('admin.customDocuments.placeholders.form.idDerived', 'Auto-derived from label. Immutable after saving.')
              : `Token: {{custom.${id || '...'}}}`}
          </span>
        </div>

        {!isEditMode && (
          <div className="form-group">
            <label>{t('admin.customDocuments.placeholders.form.type', 'Type')}</label>
            <div className="radio-group">
              {(['TEXT', 'IMAGE'] as CustomPlaceholderType[]).map((opt) => (
                <label key={opt} className="radio-label">
                  <input
                    type="radio"
                    name="placeholderType"
                    value={opt}
                    checked={type === opt}
                    onChange={() => setType(opt)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        )}

        {type !== 'IMAGE' && (
          <div className="form-group">
            <label>{t('admin.customDocuments.placeholders.form.value', 'Value')}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="form-input"
            />
          </div>
        )}

        {type === 'IMAGE' && (
          <div className="form-group">
            <label>{t('admin.customDocuments.placeholders.form.imageUpload', 'Image file (PNG or JPEG, max 2 MB)')}</label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={handleImageChange}
              className="form-input"
            />
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="image-preview" />
            )}
          </div>
        )}

        {error && <div className="form-error">{error}</div>}
      </div>

      <div className="form-panel-footer">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
          {t('admin.customDocuments.placeholders.form.cancel', 'Cancel')}
        </button>
        <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? t('admin.customDocuments.placeholders.form.saving', 'Saving...') : t('admin.customDocuments.placeholders.form.save', 'Save')}
        </button>
      </div>
    </div>
  );
};

export default CustomPlaceholderForm;
